import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  EventEmitter,
  Output,
  ViewChild,
  ElementRef,
} from '@angular/core';
import MetisMenu from 'metismenujs';
import { Router, NavigationEnd } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ModulesService, PermissionsService, MenuItem, TokenUser, IPendingCounts } from '@shared';
import { TokenService } from 'app/core/services/token.service';
import { environment } from '@env/environment';
import { AuthenticationService } from 'app/core/services/auth.service';
import { Observable, Subject, Subscription } from 'rxjs';
import { CountsService } from '@services';
import { ToastrService } from 'ngx-toastr';
import { Store } from '@ngrx/store';
import { pendingCountsState } from '@app/core/store/pendingCounts/pendingCounts.reducer';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('sideMenu') sideMenu!: ElementRef;
  eventsSubject: Subject<void> = new Subject<void>();
  menu: any;
  menuItems: MenuItem[] = [];
  permissionKey: string = environment.permissionKey;
  baseUrl = environment.publicUrl + 'public/';
  mainLogo = 'assets/images/logo/logo.png';
  isDevEnv = environment.isDevEnv;
  mainLogoDark = 'assets/images/logo/main_logo.png';
  mainLogoSmall = 'assets/images/logo/main_logo_sm.png';
  currentTheme: string | null = null;
  _currentUser$: Observable<any>;
  _currentUser: TokenUser | undefined;
  pendingCounts!: IPendingCounts;
  pendingCounts$!: Observable<IPendingCounts>;
  private menuObserver?: MutationObserver;
  @Output() mobileMenuButtonClicked = new EventEmitter();

  constructor(
    private router: Router,
    public translate: TranslateService,
    public _moduleService: ModulesService,
    public _tokenService: TokenService,
    public _permissionService: PermissionsService,
    private _authService: AuthenticationService,
    private _router: Router,
    private _countsService: CountsService,
    private _toastService: ToastrService,
    private _store: Store<{ pendingCounts: pendingCountsState }>,
  ) {
    this._currentUser$ = this._tokenService.getCurrentUser();
    translate.setDefaultLang('en');
    this.router.events.forEach((event) => {
      if (event instanceof NavigationEnd) {
        this._activateMenuDropdown();
      }
    });
    this.pendingCounts$ = this._store.select((state) => state.pendingCounts.pendingCounts);
  }

  // ngOnInit(): void {
  //   this.getAllModules();
  //   // setTimeout(() => {
  //   //   this.setSideMenuClasses();
  //   // }, 1400);
  //   this._currentUser = this._tokenService.getCurrentUser();
  // }

  ngOnInit(): void {
    this.getAllModules();
    this._currentUser$.subscribe((data) => (this._currentUser = data));
    this.pendingCounts$.subscribe((data) => {
      this.pendingCounts = data;
    });
  }

  ngAfterViewInit(): void {
    this.observeMenuForLateLinks();
    this._activateMenuDropdown();
  }

  ngOnDestroy(): void {
    this.menuObserver?.disconnect();
  }

  getAllModules() {
    this.eventsSubject.next();
    // call the modules here
  }

  toggleMobileMenu(event: any) {
    event.preventDefault();
    this.mobileMenuButtonClicked.emit();
  }

  setSideMenuClasses() {
    if (document.getElementById('side-menu')) {
      this.menu = new MetisMenu('#side-menu');
      this._activateMenuDropdown();
    }
  }
  hasItems(item: any) {
    return item.submodules !== undefined ? item.submodules.length > 0 : false;
  }

  _removeAllClass(className: any) {
    const els = document.getElementsByClassName(className);
    while (els[0]) {
      els[0].classList.remove(className);
    }
  }
  slashes = (string: string): number => string.match(/\//g)?.length ?? 0;
  returnUrlwithIndex(url: string): string {
    const slashIndex = url.lastIndexOf('/');
    const slashedUrl = url.substr(0, slashIndex).toString();
    const links: any = document.getElementsByClassName('side-nav-link-ref');
    const paths = this.menuPaths(links);
    const itemIndex = paths.indexOf(slashedUrl);

    if (itemIndex === -1 && slashedUrl.split('/').length > 1) {
      return this.returnUrlwithIndex(slashedUrl);
    } else {
      return slashedUrl;
    }
  }
  /**
   * The path each menu link points at, aligned index-for-index with `links`.
   *
   * 🚨 A link whose `routerLink` has not been resolved yet carries **no `href` attribute**, and
   * `a.href` is then the empty string — so the original `'/' + ''` produced **`'/'`**, which is the
   * dashboard's own route. Any such half-rendered link therefore stole the dashboard's highlight.
   * Those entries are `null` here so they can never match a URL, while keeping the indices aligned.
   */
  private menuPaths(links: any): (string | null)[] {
    const paths: (string | null)[] = [];
    for (let i = 0; i < links.length; i++) {
      const href = links[i].getAttribute('href');
      paths.push(href ? '/' + links[i]['href'].replace(links[i]['baseURI'], '').replace('#/', '') : null);
    }
    return paths;
  }

  /**
   * Re-run the highlight whenever the menu's contents change.
   *
   * 🚨 Every gated item renders **after** `NavigationEnd` has already fired, because
   * `*ngxPermissionsOnly` resolves asynchronously. The single activation pass the router triggers
   * therefore runs against a menu that is still nearly empty, and the item for the current route
   * usually does not exist yet — which is why the dashboard was left unhighlighted. Watching
   * `childList` only is deliberate: this method changes *classes*, so an attribute-watching observer
   * would re-trigger itself forever.
   */
  private observeMenuForLateLinks(): void {
    const menu = document.getElementById('side-menu');
    if (!menu || typeof MutationObserver === 'undefined') {
      return;
    }
    this.menuObserver = new MutationObserver(() => this._activateMenuDropdown());
    this.menuObserver.observe(menu, { childList: true, subtree: true });
  }

  _activateMenuDropdown() {
    this._removeAllClass('mm-active');
    this._removeAllClass('mm-show');
    const links: any = document.getElementsByClassName('side-nav-link-ref');
    if (links.length > 0) {
      let menuItemEl = null;
      const paths = this.menuPaths(links);
      const itemIndex = paths.indexOf(this._router.url);
      if (itemIndex === -1) {
        menuItemEl = links[paths.indexOf(this.returnUrlwithIndex(this._router.url))];
      } else {
        menuItemEl = links[itemIndex];
      }
      if (menuItemEl) {
        menuItemEl.classList.add('active');
        const parentEl = menuItemEl.parentElement;
        if (parentEl) {
          parentEl.classList.add('mm-active');
          const parent2El = parentEl.parentElement.closest('ul');
          if (parent2El && parent2El.id !== 'side-menu') {
            parent2El.classList.add('mm-show');
            const parent3El = parent2El.parentElement;
            if (parent3El && parent3El.id !== 'side-menu') {
              parent3El.classList.add('mm-active');
              const childAnchor = parent3El.querySelector('.has-arrow');
              const childDropdown = parent3El.querySelector('.has-dropdown');
              if (childAnchor) {
                childAnchor.classList.add('mm-active');
              }
              if (childDropdown) {
                childDropdown.classList.add('mm-active');
              }
              const parent4El = parent3El.parentElement;
              if (parent4El && parent4El.id !== 'side-menu') {
                parent4El.classList.add('mm-show');
                const parent5El = parent4El.parentElement;
                if (parent5El && parent5El.id !== 'side-menu') {
                  parent5El.classList.add('mm-active');
                  const childanchor = parent5El.querySelector('.is-parent');
                  if (childanchor && parent5El.id !== 'side-menu') {
                    childanchor.classList.add('mm-active');
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  logout() {
    this._authService.logout();
  }
}
